import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { WorkingTimeDialog } from "src/components/Dialogs/WorkingTimeDialog/WorkingTimeDialog";
import { RightWorkingTimeDrawer } from "src/components/RightDrawer/RightWorkingTimeDrawer";
import { WorkingTimeSearchTypes } from "src/components/ToggleBox/ToggleBox.types";
import { UserRoles } from "src/constants/user";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  getFilteredSchedules,
  getSchedules,
  updateSearchParams,
} from "src/store/reducers/schedulesSlice";
import { elementStyle } from "src/utils/helpers";
import { getSearchParams } from "src/utils/search";

import { EmptyPageState, MainPage, Table, ToggleBox } from "../../components";
import {
  ChevronDown,
  ChevronUp,
  SearchIcon,
} from "../../Icons";
import {
  showWorkingTimeDialog,
} from "../../store/reducers/dialogSlice";
import { openDrawer } from "../../store/reducers/drawerSlice";
import { setSelectedRow } from "../../store/reducers/tableSlice";
import { SortByMenuStyleProps } from "../CommonComponentsStyles";

import { columns } from "./WorkTimeScreen.data";
import { SortTypes } from "./WorkTimeScreen.types";


export const WorkingTimeScreen = () => {
  const dispatch = useAppDispatch();

  const { user } = useSelector((state: any) => state.auth);
  // @ts-ignore
  const role = UserRoles[user?.role];

  const { isWorkingTimeDialogOpen } = useSelector((state: any) => state.dialog);
  const {
    schedules,
    filteredSchedules,
    searchParams: isSearching,
    isSchedulesLoading,
    isFilteredSchedulesLoading,
    selectedToggleBoxValue: toogleBoxValue,
    searchInputValue: searchValue,
    sortByValue: sortValue,
  } = useAppSelector((state: any) => state.schedules);

  const [selectedToggleBox, setSelectedToggleBox] =
    useState<string>(toogleBoxValue);
  const [searchInput, setSearchInput] = useState(searchValue);
  const [sortBy, setSortBy] = useState<SortTypes>(sortValue);
  const [isSortSelectOpen, setIsSortSelectOpen] = useState<boolean>(false);

  const paramsCatalogue = {
    sortParams: {
      [SortTypes.Id]: 'id',
      [SortTypes.Name]: 'title',
      [SortTypes.Description]: 'description',
      [SortTypes.updatedAt]: 'updatedAt',
    },
    searchParams: {
      [WorkingTimeSearchTypes.NAME]: 'title',
      [WorkingTimeSearchTypes.STUDIO]: 'studio',
    },
  };

  const handleChange = (nextSelected: string) => {
    setSelectedToggleBox(nextSelected);
  };

  const handleOpenDialog = () => {
    dispatch(showWorkingTimeDialog());
  };

  const handleChangeSortBy = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as SortTypes);
    dispatch(updateSearchParams({ selectedToggleBox, searchInput, sortBy }));
    dispatch(
      getFilteredSchedules(
        getSearchParams(
          paramsCatalogue,
          event.target.value,
          selectedToggleBox,
          searchInput
        )
      )
    );
  };

  const handleSearchClick = () => {
    dispatch(updateSearchParams({ selectedToggleBox, searchInput, sortBy }));
    dispatch(
      getFilteredSchedules(
        getSearchParams(
          paramsCatalogue,
          sortBy,
          selectedToggleBox,
          searchInput)
      )
    );
  };

  const onRowClick = (params: any) => {
    dispatch(setSelectedRow(params.row));
    dispatch(openDrawer());
  };

  useEffect(() => {
    dispatch(getSchedules());
  }, []);

  const addButtonText = "Add schedule";
  return (
    <MainPage
      title="Schedule template"
      addButtonText={addButtonText}
      isDialogOpen={isWorkingTimeDialogOpen}
      isShouldShowEmptyState={!schedules?.length && !isSchedulesLoading}
      onAddButtonClick={handleOpenDialog}
      hiddenElement={() =>
        elementStyle({
          role,
          roleType: "ADMIN",
          firstOption: "none",
          lastOption: "inline-flex",
        })
      }
      DialogComp={<WorkingTimeDialog isOpen={isWorkingTimeDialogOpen} />}
      DrawerComp={<RightWorkingTimeDrawer />}
      EmptyStateComp={
        <EmptyPageState
          image="/images/empty-page-states/working-time.png"
          title="Currently, there are no defined working hours or schedules"
          subTitle={
            "To effectively manage your salon's operating hours, you can set up working time slots for each studio, define breaks, and allocate staff members to specific time frames."
          }
          hiddenElement={() =>
            elementStyle({
              role,
              roleType: "ADMIN",
              firstOption: "none",
              lastOption: "inline-flex",
            })
          }
          buttonText={addButtonText}
          onButtonClick={handleOpenDialog}
        />
      }
    >
      <main className="main-page">
        <Box
          display="flex"
          style={{
            padding: "24px 16px 0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: 1,
            }}
          >
            <Box
              sx={{
                border: '1px solid #D6D6DB',
                borderBottomLeftRadius: '8px',
                borderTopLeftRadius: '8px',
                borderRight: 'none',
                display: 'flex',
                alignItems: 'center',
                paddingX: 2,
                paddingY: 1,
                color: '#1F1F1F',
                fontWeight: '500',
              }}
            >
              <ToggleBox
                label="Name"
                onSelected={handleChange}
                selected={selectedToggleBox}
              />
            </Box>
            <Box
              display="flex"
              sx={{
                border: '1px solid #D6D6DB',
                backgroundColor: '#FAFAFA',
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
                flex: 1,
                maxWidth: '432px',
              }}
            >
              <InputBase
                sx={{ ml: 2, flex: 1, fontSize: '14px' }}
                placeholder="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <IconButton type="button" onClick={handleSearchClick}>
                <SearchIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ minWidth: 120, marginLeft: "16px" }}>
            <FormControl fullWidth>
              <InputLabel id="select-sort-by-label">Sort by</InputLabel>
              <Select
                MenuProps={SortByMenuStyleProps}
                labelId="select-sort-by-label"
                id="select-sort-by"
                value={sortBy}
                label="Sort by"
                onOpen={() => setIsSortSelectOpen(true)}
                onClose={() => setIsSortSelectOpen(false)}
                onChange={handleChangeSortBy}
                IconComponent={() => (
                  <>
                    {isSortSelectOpen ? (
                      <ChevronUp fontSize="inherit" />
                    ) : (
                      <ChevronDown fontSize="inherit" />
                    )}
                  </>
                )}
                sx={{
                  borderRadius: "8px",
                  color: "#1F1F1F",
                  fontWeight: "500",
                  height: "48px",

                  "& .MuiSvgIcon-root": {
                    marginRight: "14px",
                    color: isSortSelectOpen ? "#BF295C" : "#676767",
                  },
                }}
              >
                <MenuItem disableRipple value={SortTypes.Id}>
                  {SortTypes.Id}
                </MenuItem>
                <MenuItem disableRipple value={SortTypes.Name}>
                  {SortTypes.Name}
                </MenuItem>
                <MenuItem disableRipple value={SortTypes.Description}>
                  {SortTypes.Description}
                </MenuItem>
                <MenuItem disableRipple value={SortTypes.updatedAt}>
                  {SortTypes.updatedAt}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box
          style={{
            padding: "32px 16px",
            paddingBottom: schedules.length === 0 ? "72px" : undefined,
          }}
        >
          <Table
            rows={isSearching ? filteredSchedules : schedules}
            columns={columns}
            loading={isSchedulesLoading || isFilteredSchedulesLoading}
            onRowClick={onRowClick}
          />
        </Box>
      </main>
    </MainPage>
  );
};
